import clinic from "./clinic.json"
import google from "./google.json"

// Names are first name plus last initial; full names must never enter the repo.
export const googleSummary = google

const nameKey = (name) =>
  (name || "").normalize("NFKC").trim().replace(/\s+/g, " ").toLowerCase()

export function isGoogleReviewReady(review) {
  if (
    review.source !== "google" ||
    review.rating !== 5 ||
    !nameKey(review.author)
  )
    return false
  if (!["nl", "en"].every((locale) => review.body?.[locale]?.trim()))
    return false
  return !review.url || !!googleReviewUrl(review)
}

export function googleReviewUrl(review) {
  if (review.source !== "google" || !review.url) return null
  try {
    const url = new URL(review.url)
    if (url.protocol !== "https:") return null
    if (url.hostname === "maps.app.goo.gl")
      return url.pathname.length > 1 ? review.url : null
    if (
      !["www.google.com", "google.com", "maps.google.com"].includes(
        url.hostname
      )
    )
      return null
    return url.pathname.startsWith("/maps/") &&
      !url.pathname.startsWith("/maps/search")
      ? review.url
      : null
  } catch {
    return null
  }
}

export function newestFirst(reviews) {
  return reviews
    .map((review, index) => ({ review, index }))
    .sort((a, b) => {
      const dateA = a.review.sortDate || a.review.date || ""
      const dateB = b.review.sortDate || b.review.date || ""
      return dateB.localeCompare(dateA) || a.index - b.index
    })
    .map(({ review }) => review)
}

export const testimonials = newestFirst([
  ...google.reviews.filter(isGoogleReviewReady),
  ...clinic,
])

// Defaults to the capture date so the server render and hydration agree.
export function formatReviewDate(
  review,
  locale,
  referenceDate = google.snapshotDate
) {
  const date = review.sortDate || review.date
  if (!date) return ""
  const now = new Date(`${referenceDate}T00:00:00Z`)
  const calendarDate =
    date.length === 4
      ? `${date}-01-01`
      : date.length === 7
      ? `${date}-01`
      : date
  const start = new Date(`${calendarDate}T00:00:00Z`)
  if (Number.isNaN(now.getTime()) || Number.isNaN(start.getTime())) return ""

  let count, unit
  if (date.length === 4) {
    count = Math.max(0, now.getUTCFullYear() - start.getUTCFullYear())
    unit = "year"
  } else {
    const days = Math.max(0, Math.floor((now - start) / 86400000))
    let months =
      (now.getUTCFullYear() - start.getUTCFullYear()) * 12 +
      now.getUTCMonth() -
      start.getUTCMonth()
    const monthEnd = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0)
    ).getUTCDate()
    if (now.getUTCDate() < Math.min(start.getUTCDate(), monthEnd)) months--
    if (months >= 12) {
      count = Math.floor(months / 12)
      unit = "year"
    } else if (months >= 1) {
      count = months
      unit = "month"
    } else if (days >= 7) {
      count = Math.floor(days / 7)
      unit = "week"
    } else {
      count = days
      unit = "day"
    }
  }

  const nl = locale === "nl"
  const dutchUnits = {
    day: ["dag", "dagen"],
    week: ["week", "weken"],
    month: ["maand", "maanden"],
    year: ["jaar", "jaar"],
  }
  const label =
    count === 0
      ? unit === "year"
        ? nl
          ? "dit jaar"
          : "this year"
        : nl
        ? "vandaag"
        : "today"
      : nl
      ? `${count === 1 ? "een" : count} ${
          dutchUnits[unit][count === 1 ? 0 : 1]
        } geleden`
      : `${count === 1 ? "a" : count} ${unit}${count === 1 ? "" : "s"} ago`
  return /^Edited\s/i.test(review.relativeDate || "")
    ? `${nl ? "Bewerkt:" : "Edited"} ${label}`
    : label
}

export function translationLabel(review, locale) {
  if (review.originalLanguage === locale) return ""
  const labels = {
    en: { nl: "Translated from Dutch", fr: "Translated from French" },
    nl: { en: "Vertaald uit het Engels", fr: "Vertaald uit het Frans" },
  }
  return labels[locale]?.[review.originalLanguage] || ""
}
